import {
  computed,
  isRef,
  shallowReactive,
  type Component,
  type ComputedRef,
  type Ref,
} from 'vue';

type HookCallback = (...args: unknown[]) => unknown;

interface HookEntry {
  namespace: string;
  callback: HookCallback;
  priority: number;
}

export type VeyoHookSlotRender =
  | { component: Component; props?: Record<string, unknown> }
  | HTMLElement;

export interface VeyoHookSlotEntry {
  namespace: string;
  render: (context: Record<string, unknown>) => VeyoHookSlotRender | null | undefined;
  priority?: number;
}

const filters = new Map<string, Map<string, HookEntry>>();
const actions = new Map<string, Map<string, HookEntry>>();
const hookSlots = shallowReactive(new Map<string, Map<string, VeyoHookSlotEntry>>());

function sortedEntries(map: Map<string, HookEntry> | undefined): HookEntry[] {
  return [...(map?.values() ?? [])].sort((a, b) => a.priority - b.priority);
}

function getOrCreateHookMap<T>(registry: Map<string, Map<string, T>>, name: string): Map<string, T> {
  let map = registry.get(name);
  if (!map) {
    map = new Map<string, T>();
    registry.set(name, map);
  }
  return map;
}

function normalizePriority(priority?: number): number {
  return Number.isFinite(priority) ? Number(priority) : 10;
}

export function addFilter(
  name: string,
  namespace: string,
  callback: HookCallback,
  priority = 10,
): () => void {
  if (!name || !namespace || typeof callback !== 'function') {
    return () => {};
  }

  getOrCreateHookMap(filters, name).set(namespace, {
    namespace,
    callback,
    priority: normalizePriority(priority),
  });

  return () => removeFilter(name, namespace);
}

export function removeFilter(name: string, namespace?: string): void {
  if (!namespace) {
    filters.delete(name);
    return;
  }
  const map = filters.get(name);
  map?.delete(namespace);
  if (map && map.size === 0) {
    filters.delete(name);
  }
}

export function applyFilters<T>(name: string, value: T, ...args: unknown[]): T {
  let nextValue: unknown = value;
  for (const entry of sortedEntries(filters.get(name))) {
    nextValue = entry.callback(nextValue, ...args);
  }
  return nextValue as T;
}

export function addAction(
  name: string,
  namespace: string,
  callback: HookCallback,
  priority = 10,
): () => void {
  if (!name || !namespace || typeof callback !== 'function') {
    return () => {};
  }

  getOrCreateHookMap(actions, name).set(namespace, {
    namespace,
    callback,
    priority: normalizePriority(priority),
  });

  return () => removeAction(name, namespace);
}

export function removeAction(name: string, namespace?: string): void {
  if (!namespace) {
    actions.delete(name);
    return;
  }
  const map = actions.get(name);
  map?.delete(namespace);
  if (map && map.size === 0) {
    actions.delete(name);
  }
}

export function doAction(name: string, ...args: unknown[]): void {
  for (const entry of sortedEntries(actions.get(name))) {
    entry.callback(...args);
  }
}

export function registerSlot(
  name: string,
  namespace: string,
  render: VeyoHookSlotEntry['render'],
  priority = 10,
): () => void {
  if (!name || !namespace || typeof render !== 'function') {
    return () => {};
  }

  getOrCreateHookMap(hookSlots, name).set(namespace, {
    namespace,
    render,
    priority: normalizePriority(priority),
  });

  return () => removeSlot(name, namespace);
}

export function removeSlot(name: string, namespace?: string): void {
  if (!namespace) {
    hookSlots.delete(name);
    return;
  }
  const map = hookSlots.get(name);
  map?.delete(namespace);
  if (map && map.size === 0) {
    hookSlots.delete(name);
  }
}

export function getSlotEntries(name: string): VeyoHookSlotEntry[] {
  return [...(hookSlots.get(name)?.values() ?? [])].sort(
    (a, b) => (a.priority ?? 10) - (b.priority ?? 10),
  );
}

export function useHookSlotEntries(name: string | Ref<string>): ComputedRef<VeyoHookSlotEntry[]> {
  return computed(() => getSlotEntries(isRef(name) ? name.value : name));
}

